import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Phone, Mail, FileText, DollarSign, MessageSquare, Star, CheckCircle2 } from "lucide-react";
import { ExpandableText } from "@/components/ui/expandable-text";
import { CatchMeUpButton } from "@/components/catch-me-up-button";
import type { Client } from "@shared/schema";

type EraFilter = "all" | "lead" | "client";

interface UnifiedActivity {
  id: string;
  type: string;
  notes: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string | null;
  source: "lead" | "invoice";
  sourceId: string;
  invoiceNumber?: string;
  era?: "lead" | "client";
  opened?: boolean;
}

interface ClientActivityDialogProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

function getActivityIcon(type: string) {
  switch (type) {
    case "call":
      return <Phone className="w-4 h-4" />;
    case "email":
    case "email_sent":
      return <Mail className="w-4 h-4" />;
    case "payment":
    case "payment_received":
      return <CheckCircle2 className="w-4 h-4" />;
    case "review":
      return <Star className="w-4 h-4" />;
    case "note":
    default:
      return <MessageSquare className="w-4 h-4" />;
  }
}

function getActivityLabel(type: string) {
  switch (type) {
    case "call":
      return "Call";
    case "email":
    case "email_sent":
      return "Email";
    case "payment":
    case "payment_received":
      return "Marked Paid";
    case "review":
      return "Show Review";
    case "note":
      return "Note";
    default:
      return type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  }
}

function getSourceBadge(source: "lead" | "invoice", invoiceNumber?: string) {
  if (source === "lead") {
    return <Badge variant="outline" className="text-xs">Lead</Badge>;
  }
  return (
    <Badge variant="outline" className="text-xs">
      Invoice {invoiceNumber ? `#${invoiceNumber}` : ""}
    </Badge>
  );
}

function getEraBadge(era?: "lead" | "client") {
  if (!era) return null;
  if (era === "lead") {
    return (
      <Badge
        variant="outline"
        className="text-xs border-amber-500/40 text-amber-700 dark:text-amber-300"
        data-testid="badge-era-lead"
      >
        Lead era
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-xs border-blue-500/40 text-blue-700 dark:text-blue-300"
      data-testid="badge-era-client"
    >
      Client era
    </Badge>
  );
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ClientActivityDialog({ client, isOpen, onClose }: ClientActivityDialogProps) {
  const [eraFilter, setEraFilter] = useState<EraFilter>("all");

  const { data: activities = [], isLoading } = useQuery<UnifiedActivity[]>({
    queryKey: ["/api/clients", client?.id, "activities"],
    queryFn: async () => {
      if (!client?.id) return [];
      const response = await fetch(`/api/clients/${client.id}/activities`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch activities");
      return response.json();
    },
    enabled: isOpen && !!client?.id,
  });

  const counts = useMemo(() => {
    let lead = 0;
    let clientEra = 0;
    for (const a of activities) {
      if (a.era === "lead") lead++;
      else if (a.era === "client") clientEra++;
    }
    return { all: activities.length, lead, client: clientEra };
  }, [activities]);

  const visibleActivities = useMemo(() => {
    if (eraFilter === "all") return activities;
    return activities.filter(a => a.era === eraFilter);
  }, [activities, eraFilter]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Activity Timeline
            {client?.accountName && (
              <span className="text-muted-foreground font-normal">
                — {client.accountName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 pb-2 border-b">
          <span className="text-xs text-muted-foreground mr-1">Filter:</span>
          {(["all", "lead", "client"] as const).map(value => {
            const label =
              value === "all" ? `All (${counts.all})`
              : value === "lead" ? `Lead era (${counts.lead})`
              : `Client era (${counts.client})`;
            return (
              <Button
                key={value}
                variant={eraFilter === value ? "default" : "outline"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setEraFilter(value)}
                data-testid={`filter-era-${value}`}
              >
                {label}
              </Button>
            );
          })}
          {client?.id && (
            <div className="ml-auto">
              <CatchMeUpButton
                entityType="client"
                entityId={client.id}
                eventCount={activities.length}
              />
            </div>
          )}
        </div>

        <ScrollArea className="h-[500px] pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : visibleActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mb-3 opacity-50" />
              <p>{activities.length === 0 ? "No activity recorded yet" : "No activity for this filter"}</p>
              <p className="text-sm mt-1">
                {activities.length === 0
                  ? "Activities will appear here as they are logged"
                  : "Try a different era filter"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {visibleActivities.map((activity, index) => (
                <div
                  key={activity.id}
                  className="relative pl-8 pb-4"
                  data-testid={`activity-item-${index}`}
                >
                  {index < visibleActivities.length - 1 && (
                    <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-border" />
                  )}
                  <div className="absolute left-0 top-0 w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="bg-card border rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge 
                          variant={activity.type === "payment" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {getActivityLabel(activity.type)}
                        </Badge>
                        {(activity.type === "email" || activity.type === "email_sent") && activity.opened !== undefined && (
                          activity.opened ? (
                            <Badge
                              variant="outline"
                              className="text-xs border-green-500/40 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
                              data-testid={`badge-email-opened-${activity.id}`}
                            >
                              Opened
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-xs text-muted-foreground"
                              data-testid={`badge-email-unopened-${activity.id}`}
                            >
                              Unopened
                            </Badge>
                          )
                        )}
                        {getSourceBadge(activity.source, activity.invoiceNumber)}
                        {getEraBadge(activity.era)}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(activity.createdAt)}
                      </span>
                    </div>
                    {activity.notes && (
                      <div className="mt-1">
                        <ExpandableText text={activity.notes} className="text-sm" />
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      by {activity.createdByName || "Unknown"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
